import type { SupportRequestKind } from '../types';
import { GOAL_PLACEHOLDER } from './form-schema';

/**
 * The complete set of strings the support form shows. Presets supply a
 * `Partial` of this, and `supportFormCopy` fills the gaps from the default —
 * so the modal always receives every field.
 */
export interface SupportFormCopy {
  title: string;
  goalLabel: string;
  goalDescription: string;
  goalPlaceholder: string;
  submitLabel: string;
  successMessage: string;
}

/** What the form says when it is opened as a plain "I need help" request. */
export const DEFAULT_SUPPORT_FORM_COPY: SupportFormCopy = {
  title: 'Describe your support request',
  goalLabel: 'Describe your problem',
  goalDescription: 'The more detail you can share the better we can help.',
  // The canonical one: isDefaultGoalText and the Zod refine both compare
  // against it, so it must not be forked here.
  goalPlaceholder: GOAL_PLACEHOLDER,
  submitLabel: 'Send request',
  successMessage: "Your support request has been submitted. We'll get back to you soon.",
};

/**
 * Copy keyed by request kind, rather than per-field overrides at the call site.
 *
 * A caller picks a named shape and gets wording that has been written and
 * reviewed once. Letting each call site pass its own title and labels would let
 * the same form drift into a different voice everywhere it is opened.
 */
const PRESETS = {
  verification_reply: {
    title: 'Reply to account verification',
    goalLabel: 'Your reply',
    goalDescription: 'Answer what we asked for above. Your reply goes back to the team reviewing the account.',
    // A noun phrase, per the content guidelines: placeholders describe, they do
    // not instruct. The description above does the instructing.
    goalPlaceholder: 'Your reply',
    submitLabel: 'Send reply',
    successMessage: "Your reply has been sent. We'll pick the review back up.",
  },
} satisfies Record<SupportRequestKind, Partial<SupportFormCopy>>;

/** The copy for a kind, with anything it does not override filled from the default. */
export function supportFormCopy(kind: SupportRequestKind | undefined): SupportFormCopy {
  if (kind == null) return DEFAULT_SUPPORT_FORM_COPY;
  return { ...DEFAULT_SUPPORT_FORM_COPY, ...PRESETS[kind] };
}
